// WHAT: Widget component for displaying Withings body composition metrics
// WHY:  Provides dashboard overview of Withings health data with authentication check
// DOMAIN: Core → Dashboard → Widgets → Health
// DEPENDENCIES: WithingsMetricsCard, useWithingsAuth hook
// RISKS:
//   - NOTE: Authentication check may not reflect actual connection status
//   - NOTE: Error message is hardcoded in English - may not match user language
//   - NOTE: Empty divs are rendered in loading/error states - may cause layout issues

import { Scale } from "lucide-react";
import { type FC } from "react";

import { WithingsMetricsCard } from "@/domains/health/dashboard/components/molecules/WithingsMetricsCard";
import { useWithingsAuth } from "@/domains/health/withings/hooks/useWithingsAuth";

import { useWidgetConfig } from "../../../hooks/useWidgetConfig";
import { WIDGET_IDS } from "../../../store/widgetRegistry";
import { WidgetContainer } from "../base";

/**
 * WHAT:
 *   Widget component that displays Withings body composition metrics with
 *   authentication and initialization checks.
 *
 * WHY:
 *   Enables users to see their Withings health data at a glance on the dashboard.
 *
 * BEHAVIOR:
 *   - Checks authentication status
 *   - Shows loading state during initialization
 *   - Displays error if not authenticated
 *   - Renders WithingsMetricsCard when authenticated
 *
 * RISKS:
 *   - NOTE: Authentication check may not reflect actual connection status
 *   - NOTE: Error message is hardcoded in English - may not match user language
 *   - NOTE: Empty divs are rendered in loading/error states - may cause layout issues
 *
 * @returns {JSX.Element} Withings summary widget component
 *
 */
export const WithingsSummaryWidget: FC = () => {
  const { config } = useWidgetConfig(WIDGET_IDS.WITHINGS_SUMMARY);
  const { isAuthenticated, isInitialized } = useWithingsAuth();

  if (!isInitialized) {
    return (
      <WidgetContainer
        description="Body composition"
        icon={<Scale className="w-5 h-5" />}
        loading
        size={config?.size ?? "medium"}
        title="Withings Summary"
        widgetId={WIDGET_IDS.WITHINGS_SUMMARY}
      >
        <div />
      </WidgetContainer>
    );
  }

  if (!isAuthenticated) {
    return (
      <WidgetContainer
        description="Body composition"
        error="Not authenticated. Please connect Withings in settings."
        icon={<Scale className="w-5 h-5" />}
        size={config?.size ?? "medium"}
        title="Withings Summary"
        widgetId={WIDGET_IDS.WITHINGS_SUMMARY}
      >
        <div />
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer
      description="Body composition"
      icon={<Scale className="w-5 h-5" />}
      size={config?.size ?? "medium"}
      title="Withings Summary"
      widgetId={WIDGET_IDS.WITHINGS_SUMMARY}
    >
      <WithingsMetricsCard />
    </WidgetContainer>
  );
};
